import { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { request } from '../../../shared/services/api';

interface Peticion {
  id: string;
  codigo: string;
  estado: 'BORRADOR' | 'PENDIENTE' | 'APROBADA' | 'RECHAZADA' | 'CANCELADA';
  tipoPeticion: { id: string; nombre: string };
  solicitante: { id: string; nombreCompleto: string; email: string };
  aprobadorActual: { id: string; nombreCompleto: string } | null;
  datosFormulario: Record<string, unknown>;
  creadoEn: string;
  actualizadoEn: string;
  aprobaciones?: Array<{
    id: string;
    decision: 'APROBADA' | 'RECHAZADA';
    comentarios: string | null;
    fechaDecision: string;
    aprobador: { id: string; nombreCompleto: string; email: string };
  }>;
}

const ESTADOS_FILTRO = [
  { value: '', label: 'Todas' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'APROBADA', label: 'Aprobada' },
  { value: 'RECHAZADA', label: 'Rechazada' },
  { value: 'BORRADOR', label: 'Borrador' },
];

const ESTADO_COLORS: Record<string, { color: string; bg: string }> = {
  PENDIENTE: { color: '#C05621', bg: '#FEEBC8' },
  APROBADA: { color: '#276749', bg: '#C6F6D5' },
  RECHAZADA: { color: '#9B2C2C', bg: '#FED7D7' },
  BORRADOR: { color: '#718096', bg: '#EDF2F7' },
  CANCELADA: { color: '#4A5568', bg: '#E2E8F0' },
};

function tiempoRelativo(fecha: string): string {
  const diff = Date.now() - new Date(fecha).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Ahora';
  if (min < 60) return `Hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Hace ${h}h`;
  return `Hace ${Math.floor(h / 24)}d`;
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function PeticionesPage() {
  const [items, setItems] = useState<Peticion[]>([]);
  const [selected, setSelected] = useState<Peticion | null>(null);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filtro ? `?estado=${filtro}` : '';
      const res = await request<{ data: Peticion[]; meta: { total: number } }>(
        `/apps/peticiones${qs}`,
      );
      setItems(res.data);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  const fetchDetail = useCallback(async (id: string) => {
    try {
      const res = await request<{ data: Peticion }>(`/apps/peticiones/${id}`);
      setSelected(res.data);
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const doAction = async (id: string, action: 'enviar' | 'aprobar' | 'rechazar') => {
    if (acting) return;
    let body: Record<string, unknown> = {};
    if (action === 'rechazar') {
      const motivo = window.prompt('Motivo del rechazo:');
      if (!motivo) return;
      body = { comentarios: motivo };
    }
    setActing(true);
    try {
      await request(`/apps/peticiones/${id}/${action}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await fetchList();
      await fetchDetail(id);
    } catch {
      /* silencioso */
    } finally {
      setActing(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Panel izquierdo — lista */}
      <div
        style={{
          width: 300,
          flexShrink: 0,
          borderRight: '1px solid #E8E8E8',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
        }}
      >
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #F0F0F0' }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1F3864', margin: '0 0 12px' }}>
            Peticiones
          </h2>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ESTADOS_FILTRO.map((e) => (
              <button
                key={e.value}
                onClick={() => setFiltro(e.value)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 14,
                  fontSize: 11,
                  fontWeight: filtro === e.value ? 600 : 400,
                  color: filtro === e.value ? '#fff' : '#4A5568',
                  background: filtro === e.value ? '#1F3864' : '#F7F7F7',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {e.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <LoadingSpinner size="sm" muted />
          ) : items.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#CBD5E0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'block', margin: '0 auto 12px' }}
              >
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
              </svg>
              <p style={{ color: '#A0AEC0', fontSize: 13, margin: 0 }}>Sin peticiones</p>
            </div>
          ) : (
            items.map((p) => {
              const ec = ESTADO_COLORS[p.estado] ?? ESTADO_COLORS.BORRADOR;
              const isActive = selected?.id === p.id;
              return (
                <div
                  key={p.id}
                  onClick={() => fetchDetail(p.id)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #F5F5F5',
                    cursor: 'pointer',
                    background: isActive ? '#EBF5FF' : '#fff',
                    borderLeft: isActive ? '3px solid #1F3864' : '3px solid transparent',
                    transition: 'background 0.1s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontWeight: 700,
                        fontSize: 13,
                        color: '#1F3864',
                      }}
                    >
                      {p.codigo}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 4,
                        fontWeight: 600,
                        color: ec.color,
                        background: ec.bg,
                      }}
                    >
                      {p.estado}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#4A5568', marginTop: 2 }}>
                    {p.tipoPeticion.nombre}
                  </div>
                  <div style={{ fontSize: 11, color: '#A0AEC0', marginTop: 2 }}>
                    {p.solicitante.nombreCompleto} · {tiempoRelativo(p.creadoEn)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Panel derecho — detalle */}
      <div style={{ flex: 1, overflow: 'auto', background: '#F8F8F8' }}>
        {!selected ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#CBD5E0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'block', margin: '0 auto 12px' }}
              >
                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
              </svg>
              <p style={{ color: '#A0AEC0', fontSize: 14 }}>Selecciona una petición</p>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, maxWidth: 700 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <span
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: 700,
                  fontSize: 18,
                  color: '#1F3864',
                }}
              >
                {selected.codigo}
              </span>
              <span style={{ fontSize: 14, color: '#4A5568' }}>{selected.tipoPeticion.nombre}</span>
              {(() => {
                const ec = ESTADO_COLORS[selected.estado] ?? ESTADO_COLORS.BORRADOR;
                return (
                  <span
                    style={{
                      fontSize: 12,
                      padding: '2px 10px',
                      borderRadius: 6,
                      fontWeight: 600,
                      color: ec.color,
                      background: ec.bg,
                    }}
                  >
                    {selected.estado}
                  </span>
                );
              })()}
              <button
                onClick={() => setSelected(null)}
                style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#A0AEC0',
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>

            {/* Datos del formulario */}
            <div
              style={{
                background: '#fff',
                borderRadius: 10,
                padding: 20,
                marginBottom: 16,
                border: '1px solid #E8E8E8',
              }}
            >
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', margin: '0 0 12px' }}>
                Datos de la solicitud
              </h3>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {Object.entries(selected.datosFormulario).map(([key, val]) => (
                    <tr key={key}>
                      <td
                        style={{
                          padding: '6px 12px 6px 0',
                          color: '#718096',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          verticalAlign: 'top',
                        }}
                      >
                        {humanizeKey(key)}
                      </td>
                      <td style={{ padding: '6px 0', color: '#1A202C' }}>{String(val ?? '—')}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ padding: '6px 12px 6px 0', color: '#718096', fontWeight: 500 }}>
                      Solicitante
                    </td>
                    <td style={{ padding: '6px 0', color: '#1A202C' }}>
                      {selected.solicitante.nombreCompleto}
                    </td>
                  </tr>
                  {selected.aprobadorActual && (
                    <tr>
                      <td style={{ padding: '6px 12px 6px 0', color: '#718096', fontWeight: 500 }}>
                        Aprobador actual
                      </td>
                      <td style={{ padding: '6px 0', color: '#1A202C' }}>
                        {selected.aprobadorActual.nombreCompleto}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Historial de aprobaciones */}
            {selected.aprobaciones && selected.aprobaciones.length > 0 && (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 10,
                  padding: 20,
                  marginBottom: 16,
                  border: '1px solid #E8E8E8',
                }}
              >
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1F3864', margin: '0 0 12px' }}>
                  Historial de aprobaciones
                </h3>
                {selected.aprobaciones.map((a) => {
                  const ec = ESTADO_COLORS[a.decision] ?? ESTADO_COLORS.BORRADOR;
                  return (
                    <div
                      key={a.id}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '8px 0',
                        borderBottom: '1px solid #F5F5F5',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontWeight: 600,
                          color: ec.color,
                          background: ec.bg,
                        }}
                      >
                        {a.decision}
                      </span>
                      <span style={{ fontSize: 13, color: '#4A5568' }}>
                        {a.aprobador.nombreCompleto}
                      </span>
                      <span style={{ fontSize: 11, color: '#A0AEC0', marginLeft: 'auto' }}>
                        {tiempoRelativo(a.fechaDecision)}
                      </span>
                      {a.comentarios && (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#718096',
                            fontStyle: 'italic',
                            width: '100%',
                            marginTop: 4,
                          }}
                        >
                          {a.comentarios}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Acciones */}
            {(selected.estado === 'BORRADOR' || selected.estado === 'PENDIENTE') && (
              <div style={{ display: 'flex', gap: 10 }}>
                {selected.estado === 'BORRADOR' && (
                  <button
                    disabled={acting}
                    onClick={() => doAction(selected.id, 'enviar')}
                    style={{
                      padding: '10px 24px',
                      borderRadius: 8,
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      background: '#1F3864',
                      color: '#fff',
                    }}
                  >
                    Enviar a aprobación
                  </button>
                )}
                {selected.estado === 'PENDIENTE' && (
                  <>
                    <button
                      disabled={acting}
                      onClick={() => doAction(selected.id, 'aprobar')}
                      style={{
                        padding: '10px 24px',
                        borderRadius: 8,
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        background: '#38A169',
                        color: '#fff',
                      }}
                    >
                      Aprobar
                    </button>
                    <button
                      disabled={acting}
                      onClick={() => doAction(selected.id, 'rechazar')}
                      style={{
                        padding: '10px 24px',
                        borderRadius: 8,
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        background: '#E53E3E',
                        color: '#fff',
                      }}
                    >
                      Rechazar
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
